import type { TableColumn } from '@ipfabric/utils';
import { maybePropOf, prop } from '@ipfabric/utils/fp/prop';
import type { TableMergedState } from 'components/Table/model/TableModel';
import { selectAllColumns } from 'components/Table/model/selectors/selectAllColumns';
import {
  type ColumnsByKey,
  selectColumnsByKey,
} from 'components/Table/model/selectors/selectColumnsByKey';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';

/**
 * When orderedColumns are defined, they must cover all available columns
 *
 * Otherwise, those columns won't either be visible in display settings,
 * so such settings should be ignored.
 */
const doOrderedColumnsCoverAllColumns = (
  columnsByKey: ColumnsByKey,
  orderedColumnKeys: string[],
) => {
  const set = new Set(orderedColumnKeys);
  return Object.keys(columnsByKey).every((key) => set.has(key));
};

export const selectOrderedColumns: (s: TableMergedState) => TableColumn<any>[] =
  createScopedSelector(() =>
    createSelector(
      selectAllColumns,
      selectColumnsByKey,
      prop('settings', 'orderedColumns'),
      (columns, columnsByKey, orderedColumnKeys) => {
        if (
          orderedColumnKeys === undefined ||
          !doOrderedColumnsCoverAllColumns(columnsByKey, orderedColumnKeys)
        ) {
          return columns;
        }
        return pipe(
          orderedColumnKeys,
          // column present in orderedColumnKeys but not in columns definition is considered OK,
          // we will just not display it, no need to consider settings invalid and reset
          array.filterMap(maybePropOf(columnsByKey)),
        );
      },
    ),
  );
